import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import luciusLogoWhiteUrl from '@/assets/svg/lucius-logo-white.svg';
import {
  compressBase64Image,
  VALID_IMAGE_EXTENSIONS,
} from '@/utils/image.utils';
import Lottie from 'lottie-react';

interface AvatarProps {
  imageUrl?: string;
  allowedFormats?: string[];
  loopAnimation?: boolean;
  togglesMenu?: boolean;
  showAuthorizedFormatInfo?: boolean;
  containerClassName?: string;
  onShowErrorMessage?: (message: string) => void;
  onChange?: (url: string) => void;
}

export const Avatar = ({
  imageUrl,
  allowedFormats = VALID_IMAGE_EXTENSIONS,
  loopAnimation = true,
  togglesMenu,
  showAuthorizedFormatInfo = true,
  containerClassName,
  onShowErrorMessage,
  onChange,
}: AvatarProps) => {
  const { t } = useTranslation('group');
  const [pictureUrl, setPictureUrl] = useState('');
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [jsonContent, setJsonContent] = useState<object | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadedImageIsSvg = pictureUrl?.startsWith('data:image/svg');
  const uploadedFileIsJson = jsonFile !== null;

  useEffect(() => {
    if (imageUrl) {
      setPictureUrl(imageUrl);
      // Reset JSON data when loading a new image
      setJsonFile(null);
      setJsonContent(null);

      // Check if the imageUrl contains JSON content (starts with { or [)
      if (
        typeof imageUrl === 'string' &&
        (imageUrl.startsWith('{') || imageUrl.startsWith('['))
      ) {
        try {
          const parsedJson = JSON.parse(imageUrl);
          setJsonContent(parsedJson);
          // Create a mock file object for JSON content loaded from database
          const mockFile = new File([imageUrl], 'animation.json', {
            type: 'application/json',
          });
          setJsonFile(mockFile);
        } catch (error) {
          console.error('Error parsing JSON from imageUrl:', error);
        }
      }
    }
  }, [imageUrl]);

  const handleUploaderClick = () => {
    fileInputRef.current?.click();
  };

  const isFormatAllowed = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedFormats.includes(fileExtension)) {
      const formatsText = allowedFormats.join(
        `, ${t('common:or').toLowerCase()} `
      );

      // Send error message to parent component
      onShowErrorMessage?.(
        `${t('common:formatNotAllowedHereIsTheAllowedFormat')} : ${formatsText}`
      );

      return false;
    }
    return true;
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!isFormatAllowed(file)) {
      return;
    }

    if (file) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (!fileExtension || !allowedFormats.includes(fileExtension)) {
        onShowErrorMessage?.(
          `${t('common:errorImgType')} .${allowedFormats.join(
            `, ${t('common:or').toLowerCase()} .`
          )}`
        );

        return;
      }

      const fileSizeInMB = file.size / (1024 * 1024);

      // Check if file size is greater than 4MB
      if (fileSizeInMB > 4) {
        alert(`${t('common:errorImgSize')}.`);
        return;
      }

      // Proceed with the file if the extension is valid
      if (fileExtension === 'json') {
        // For JSON files, read and parse the content for Lottie
        const textReader = new FileReader();
        textReader.onload = () => {
          try {
            const parsedContent = JSON.parse(textReader.result as string);
            setJsonContent(parsedContent);
            setJsonFile(file);
            setPictureUrl(''); // Clear picture URL for JSON files
            onChange?.(parsedContent); // Pass the parsed JSON content to parent
          } catch (error) {
            console.error('Error parsing JSON file:', error);
            onShowErrorMessage?.(
              'Invalid JSON file. Please check the file format.'
            );
          }
        };
        textReader.readAsText(file);
      } else {
        // For image files, use the existing logic
        const reader = new FileReader();
        reader.onload = async () => {
          let base64Url = `${reader.result}`;

          try {
            // Only compress if the file size is 1MB or higher
            if (fileSizeInMB >= 1) {
              base64Url = await compressBase64Image(base64Url, 800, 800, 0.7);
            }

            setPictureUrl(base64Url);
            setJsonFile(null); // Reset JSON file for images
            setJsonContent(null); // Reset JSON content for images
            onChange?.(base64Url);
          } catch (error) {
            console.error('Error processing image:', error);
          }
        };

        reader.readAsDataURL(file);
      }
    }
  };

  return (
    <div className={twMerge('', togglesMenu && 'menu-toggle')}>
      <div
        className={twMerge(
          'w-[111px] h-[104px] rounded-2xl border-3 border-white shadow-dark flex items-center relative avatar-container group',
          containerClassName
        )}
      >
        <div className="profile-image-wrapper bg-blue-131025 flex justify-center items-center w-full h-full rounded-xl group-[&.avatar-container.rounded-full]:rounded-full overflow-hidden">
          {uploadedFileIsJson ? (
            <Lottie
              animationData={jsonContent}
              loop={loopAnimation}
              autoplay={true}
            />
          ) : (
            <img
              className={twMerge(
                'w-full h-auto group-[&.avatar-container.rounded-full]:rounded-full object-contain object-center',
                (!pictureUrl || uploadedImageIsSvg) &&
                  'w-[70%] h-[70%] group-[&.avatar-container.rounded-full]:w-full group-[&.avatar-container.rounded-full]:h-full'
              )}
              src={pictureUrl !== '' ? pictureUrl : luciusLogoWhiteUrl}
              alt="profile-pic"
            />
          )}
        </div>
        {onChange && (
          <div
            onClick={handleUploaderClick}
            className="flex justify-center items-center w-7 h-7 bg-white rounded-md shadow-default absolute -right-3.5 hover:cursor-pointer"
          >
            <i className="-mt-0.5 ki-filled ki-notepad-edit text-slate-500 w-3.5 h-3.5"></i>
          </div>
        )}
      </div>
      {onChange && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
            accept={allowedFormats
              .map((format) => `.${format.toLowerCase()}`)
              .join(',')}
          />
          {showAuthorizedFormatInfo && (
            <p className="mt-3 text-gray-600 text-sm">
              {allowedFormats?.length > 1
                ? t('common:allowedFormats')
                : t('common:allowedFormat')}
              : {allowedFormats?.join(', ')}.
            </p>
          )}
        </>
      )}
    </div>
  );
};
